import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { CheckState, IOptionProps, IOptionCssProps, OptionTexts, OptionWrapper, useOptionAsOwnInputCss, getOptionBorderSizeCss, CheckColour } from '../Options';
import { useTestId } from '../../../hooks/useTestId/useTestId';
import { mergeRefs } from '../../../common/utils';
import { useOption } from '../useOption';
import { BaseInput } from '../../../common/BaseElements';
import { SIZES } from '../../../fundamentals/sizes/sizes';
import { useFocusCss } from '../../../fundamentals/actions';
import { generateOptionColours } from '../../../fundamentals/colours/generateUiColours';

export interface IRadioProps extends IOptionProps {}

const RadioBase = styled(BaseInput)<IOptionCssProps & { colour?: CheckColour }>`
  ${useOptionAsOwnInputCss};
  &:focus-visible {
    outline: none;
  }
  ${({ wrapped }) => !wrapped && useFocusCss()}
  &:after {
    content: '';
    border-radius: 50%;

    ${({ theme, optionSize }) => {
      const width = `calc(${theme.sizes[optionSize!]} / 2)`;
      return css`
        height: ${width};
        width: ${width};
        min-width: ${width};
      `;
    }};

    margin: ${({ theme, optionSize, disabled, colour }) => `calc(${theme.sizes[optionSize!]} / 4 - ${getOptionBorderSizeCss(disabled, colour)})`};
  }

  &:checked:after {
    ${({ disabled, theme, colour }) => {
      const colourSpecificOptions = colour === CheckColour.DEFAULT ? theme.optionColours : generateOptionColours(theme.colours, undefined, colour);

      return css`
        background: ${colourSpecificOptions[disabled ? CheckState.DISABLED : CheckState.CHECKED].text};
      `;
    }}
  }

  ${({ hasHelpText }) =>
    hasHelpText &&
    css`
      align-self: start;
    `}
`;

export const Radio = forwardRef<HTMLInputElement, IRadioProps>(
  ({ disabled, rightAligned, size = SIZES.X_SMALL, label, helpText, helpTextAsError, wrapped, colour = CheckColour.DEFAULT, ...rest }, ref) => {
    const { generateTestId } = useTestId(rest, 'Radio');

    const { inputRef, focused, handleOnWrapperClick, handleOnBlur, handleOnTextClick, handleOnFocus, testIdLabel } = useOption({ wrapped, ...rest });

    const Label = (
      <OptionTexts
        rightAligned={rightAligned}
        label={label}
        helpText={helpText}
        helpTextAsError={helpTextAsError}
        disabled={disabled}
        size={size}
        onClick={handleOnTextClick}
        {...generateTestId(testIdLabel)}
      />
    );

    return (
      <OptionWrapper
        focused={focused}
        size={size}
        wrapped={wrapped}
        disabled={disabled}
        rightAligned={rightAligned}
        onClick={handleOnWrapperClick}
        {...generateTestId('Wrapper')}
        {...rest}
      >
        {rightAligned && Label}
        <RadioBase
          optionSize={size}
          colour={colour}
          type='radio'
          disabled={disabled}
          ref={mergeRefs([inputRef, ref])}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          wrapped={wrapped}
          hasHelpText={!!helpText || !!helpTextAsError}
          {...rest}
          {...generateTestId(testIdLabel)}
        />
        {!rightAligned && Label}
      </OptionWrapper>
    );
  }
);
